import React, { useState, useEffect, useRef } from 'react';
import { walletApi } from '../services/api';

interface AddFundsModalProps {
  currentBalance: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddFundsModal: React.FC<AddFundsModalProps> = ({
  currentBalance,
  onClose,
  onSuccess,
}) => {
  const [amount, setAmount] = useState('50.00');
  const [loading, setLoading] = useState(false);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [card, setCard] = useState<any>(null);

  useEffect(() => {
    if (!cardContainerRef.current) return;

    // Load Square Web Payments SDK
    const initializeSquare = async () => {
      try {
        // Check if Square is loaded
        // @ts-ignore
        if (!window.Square) {
          console.error('Square SDK not loaded');
          alert('Square SDK not loaded. Please refresh the page.');
          return;
        }

        const appId = process.env.REACT_APP_SQUARE_APPLICATION_ID;
        const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;

        console.log('[Square] Initializing with:', { appId, locationId });

        if (!appId || !locationId) {
          console.error('Missing Square credentials');
          alert('Square not configured. Please contact support.');
          return;
        }

        // @ts-ignore - Square is loaded via script tag in index.html
        const payments = await window.Square.payments(appId, locationId);

        const cardInstance = await payments.card();
        await cardInstance.attach(cardContainerRef.current!);
        setCard(cardInstance);

        console.log('[Square] ✅ Card form loaded successfully');
      } catch (error: any) {
        console.error('Failed to initialize Square:', error);
        alert(`Failed to load payment form: ${error.message || 'Please refresh and try again'}`);
      }
    };

    // Wait a bit for Square SDK to load
    setTimeout(initializeSquare, 500);

    return () => {
      if (card) {
        card.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!card) {
      alert('Payment form not ready. Please wait a moment and try again.');
      return;
    }

    if (parseFloat(amount) < 5) {
      alert('Minimum amount is $5.00');
      return;
    }

    setLoading(true);

    try {
      // Tokenize card
      const result = await card.tokenize();

      if (result.status === 'OK') {
        // Send to backend
        const response = await walletApi.addFunds(parseFloat(amount), result.token);

        alert(`Success! $${amount} added to your wallet.\n\nNew Balance: $${response.new_balance.toFixed(2)}`);

        onSuccess();
        onClose();
      } else {
        throw new Error(result.errors?.[0]?.message || 'Payment failed');
      }
    } catch (error: any) {
      alert(`Payment failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [25, 50, 100, 250];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-strong max-w-lg w-full m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Add Funds to Wallet</h2>
          <p className="text-sm text-slate-600 mt-1">Add money to your CraftyShip wallet for USPS shipping</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Current Balance */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600">Current Balance</p>
            <p className="text-2xl font-bold text-slate-900">${currentBalance.toFixed(2)}</p>
          </div>

          {/* Amount Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Amount to Add *
            </label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {quickAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(amt.toFixed(2))}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${
                    parseFloat(amount) === amt
                      ? 'border-brand-blue bg-blue-50 text-brand-blue'
                      : 'border-slate-200 text-slate-700 hover:border-brand-blue'
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg font-semibold">
                $
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                min="5"
                placeholder="50.00"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-brand-blue"
                required
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">Minimum: $5.00</p>
          </div>

          {/* New Balance Preview */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-900 font-medium">New Balance After Payment</p>
            <p className="text-3xl font-bold text-green-600 mt-1">
              ${(currentBalance + parseFloat(amount || '0')).toFixed(2)}
            </p>
          </div>

          {/* Square Card Form */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Payment Information
            </label>
            <div
              ref={cardContainerRef}
              id="card-container"
              className="border border-slate-200 rounded-lg p-4 min-h-[120px]"
            />
            <p className="text-xs text-slate-500 mt-2">
              🔒 Secure payment powered by Square
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !card}
            className="w-full px-6 py-3 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
          >
            {loading ? 'Processing Payment...' : `Add $${amount} to Wallet`}
          </button>
        </form>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <p className="text-xs text-slate-500">
            Funds available immediately after payment
          </p>
        </div>
      </div>
    </div>
  );
};
